<template>
  <div class="pw-wrap">

    <!-- Acciones (no imprimen) -->
    <div class="no-print pw-actions">
      <button class="btn-back" @click="volver">
        <i class="bi bi-arrow-left"></i> Volver
      </button>
      <button class="btn-print" @click="imprimir" :disabled="loading || !!error">
        <i class="bi bi-printer"></i> Imprimir / Guardar PDF
      </button>
    </div>

    <!-- Cargando -->
    <div v-if="loading" class="pw-estado no-print">
      <div class="spinner"></div>
      <p>Generando proforma…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pw-estado no-print">
      <i class="bi bi-exclamation-triangle-fill" style="font-size:2rem;color:#ef4444"></i>
      <p>{{ error }}</p>
      <button class="btn-back" @click="volver">Volver</button>
    </div>

    <!-- Documento -->
    <div v-else class="sheet" id="proforma-doc">

      <!-- Barra bicolor -->
      <div class="top-bar"><div class="top-l"></div><div class="top-r"></div></div>

      <!-- Cabecera -->
      <div class="head">
        <div class="head-left">
          <img v-if="prestador.logo_url" class="logo-img" :src="prestador.logo_url" alt="logo" />
          <div class="prestador-info">
            <span class="prestador-nombre">{{ prestador.nombre }}</span>
            <span class="prestador-dato" v-if="prestador.direccion">{{ prestador.direccion }}</span>
            <span class="prestador-dato" v-if="prestador.telefono || prestador.correo">
              {{ [prestador.telefono, prestador.correo].filter(Boolean).join(' · ') }}
            </span>
            <span class="prestador-dato" v-if="prestador.rtn">RTN: {{ prestador.rtn }}</span>
            <span class="prestador-dato" v-if="prestador.web">{{ prestador.web }}</span>
          </div>
        </div>
        <div class="head-right">
          <div class="doc-title">PROFORMA</div>
          <div class="meta-block">
            <div class="meta-item">
              <span class="meta-lbl">Fecha emisión</span>
              <span class="meta-val">{{ fechaFormateada }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-lbl">N° de Cotización</span>
              <span class="meta-val">{{ data.num_proforma }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-lbl">Servicio vigente hasta</span>
              <span class="meta-val vencimiento">{{ fechaVencimiento }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cuerpo -->
      <div class="body">

        <!-- Sección cliente -->
        <div class="section-lbl">Datos de la empresa</div>
        <div class="client-grid">
          <div class="dotted-row">
            <span class="dotted-key">Empresa (Cliente):</span>
            <span class="dotted-val">{{ data.empresa?.nombre || '—' }}</span>
          </div>
          <div class="dotted-row">
            <span class="dotted-key">RTN:</span>
            <span class="dotted-val">{{ data.empresa?.rtn || '—' }}</span>
          </div>
          <div class="dotted-row">
            <span class="dotted-key">Dirección:</span>
            <span class="dotted-val">{{ data.empresa?.direccion || '—' }}</span>
          </div>
          <div class="dotted-row">
            <span class="dotted-key">Correo:</span>
            <span class="dotted-val">{{ data.empresa?.correo_principal || data.empresa?.correo || '—' }}</span>
          </div>
        </div>

        <div class="plan-badge">
          ★ &nbsp;{{ data.plan?.nombre }}
        </div>

        <!-- Tabla servicios -->
        <div class="tbl-wrap">
          <table class="svc">
            <thead>
              <tr>
                <th style="width:8%">Cant.</th>
                <th class="l" style="width:55%">Descripción del servicio</th>
                <th style="width:18%">Período</th>
                <th class="r" style="width:19%">Precio (L.)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td class="l">
                  <div class="svc-name">{{ data.plan?.nombre }}</div>
                  <div class="svc-desc" v-if="data.plan?.descripcion">{{ data.plan.descripcion }}</div>
                  <div class="chips" v-if="planFeatures.length">
                    <span v-for="f in planFeatures" :key="f" class="chip">{{ f }}</span>
                  </div>
                </td>
                <td>Mensual</td>
                <td class="r">L. {{ fmtHNL(data.precio_hnl) }}</td>
              </tr>
              <tr class="empty-row"><td></td><td class="l"></td><td></td><td class="r"></td></tr>
              <tr class="empty-row"><td></td><td class="l"></td><td></td><td class="r"></td></tr>
            </tbody>
          </table>
        </div>

      </div><!-- /body -->

      <!-- Pie -->
      <div class="foot">
        <div class="observaciones">
          <div class="obs-titulo">Observaciones</div>
          <div class="obs-body">
            El cliente dispone de <strong>5 días hábiles</strong> a partir de la fecha
            de emisión para realizar el pago de esta proforma. Transcurrido ese plazo
            sin confirmación de pago, los servicios del sistema POSWAVE serán
            <strong>deshabilitados automáticamente</strong>
            el <strong>{{ fechaBloqueo }}</strong>.<br><br>
            El pago se realiza únicamente mediante <strong>transferencia bancaria</strong>. El comprobante
            debe subirse en el módulo <em>Mi Cuenta</em> del sistema.
          </div>
        </div>
        <div class="totales">
          <div class="tot-row">
            <span class="tot-lbl">Subtotal</span>
            <span class="tot-val">L. {{ fmtHNL(data.precio_hnl) }}</span>
          </div>
          <div class="tot-row">
            <span class="tot-lbl">ISV (15%)</span>
            <span class="tot-val">L. {{ fmtHNL(isv) }}</span>
          </div>
          <div class="tot-final">
            <span class="tot-final-lbl">Total L.</span>
            <span class="tot-final-val">{{ fmtHNL(totalHNL) }}</span>
          </div>
          <div class="tot-disclaimer">
            Equivalencia: $ {{ fmtUSD(data.precio_usd) }} USD × L. {{ fmtTC(data.tipo_cambio?.compra) }}/USD
            (BCH {{ fechaTipoCambio }})
          </div>
        </div>
      </div>

      <!-- Nota legal -->
      <div class="nota">
        Este documento es una <strong>proforma</strong> — no constituye factura fiscal ni tiene validez tributaria.
        Una vez verificado el pago, se emitirá la factura formal correspondiente.
      </div>

      <!-- Barra inferior -->
      <div class="bottom-bar"></div>

    </div><!-- /sheet -->
  </div><!-- /pw-wrap -->
</template>

<script>
const { getApi } = require("../../config/getApiUrl.js");

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default {
  name: 'ProformaPage',

  data() {
    return {
      loading: true,
      error:   null,
      data:    {},
      isDark:  false,
    };
  },

  computed: {
    prestador() {
      return this.data.prestador ?? {
        nombre:    'Perdomo y Asociados S de R.L',
        rtn:       null,
        direccion: 'La Ceiba, Atlántida, Honduras',
        telefono:  '+504 9358-7280',
        correo:    'comunicaciones@perdomoyasociados.com',
        web:       'www.perdomoyasociados.com',
        logo_url:  'https://perdomoyasociados.com/wp-content/uploads/2023/09/logo_perdomo_2023_dorado-768x150.png',
      };
    },

    fechaFormateada() {
      if (!this.data.fecha_proforma) return '—';
      const d = new Date(this.data.fecha_proforma);
      return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
    },

    planFeatures() {
      const p = this.data.plan;
      if (!p) return [];
      const raw = p.modulos ?? p.features ?? p.caracteristicas ?? '';
      if (Array.isArray(raw)) return raw;
      return raw ? String(raw).split(',').map(s => s.trim()).filter(Boolean) : [];
    },

    isv()     { return Number(((this.data.precio_hnl || 0) * 0.15).toFixed(2)); },
    totalHNL(){ return Number(((this.data.precio_hnl || 0) * 1.15).toFixed(2)); },

    fechaVencimiento() {
      const fv = this.data.empresa?.fecha_vencimiento;
      if (!fv) return '—';
      const [y, m, d] = String(fv).substring(0, 10).split('-').map(Number);
      return `${d} de ${MESES[m - 1]} de ${y}`;
    },

    fechaBloqueo() {
      const fv = this.data.empresa?.fecha_vencimiento;
      if (!fv) return '—';
      const [y, m, d] = String(fv).substring(0, 10).split('-').map(Number);
      // 5 días después del vencimiento = fecha de bloqueo
      const bloqueo = new Date(y, m - 1, d + 5);
      return `${bloqueo.getDate()} de ${MESES[bloqueo.getMonth()]} de ${bloqueo.getFullYear()}`;
    },

    fechaTipoCambio() {
      const f = this.data.tipo_cambio?.fecha;
      if (!f) return '—';
      const [y, m, d] = String(f).substring(0, 10).split('-').map(Number);
      return `${d} de ${MESES[m - 1]} de ${y}`;
    },
  },

  methods: {
    fmtHNL(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtTC(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString('es-HN', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    },
    fmtUSD(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    token() { return localStorage.getItem('token') || localStorage.getItem('auth') || ''; },
    volver() { this.$router.back(); },
    imprimir() { window.print(); },

    async cargar() {
      const idPlan = this.$route.query.id_plan;
      if (!idPlan) {
        this.error   = 'No se especificó ningún plan. Vuelve y selecciona uno.';
        this.loading = false;
        return;
      }
      try {
        const r = await fetch(`${getApi()}/cuenta/proforma?id_plan=${idPlan}`, {
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.message || `Error ${r.status}`);
        }
        this.data = await r.json();
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    onToggleDark(e) { this.isDark = e.detail ?? !this.isDark; },
  },

  mounted() {
    this.isDark = document.documentElement.classList.contains('dark-mode');
    window.addEventListener('poswave:toggle-dark', this.onToggleDark);
    // Cambiar título para que el PDF no imprima "Mi Cuenta — POSWAVE"
    this._prevTitle = document.title;
    document.title  = 'Proforma — POSWAVE';
    this.cargar();
  },
  beforeUnmount() {
    window.removeEventListener('poswave:toggle-dark', this.onToggleDark);
    // Restaurar título original al salir
    if (this._prevTitle) document.title = this._prevTitle;
  },
};
</script>

<style scoped>
/* ── Wrap ── */
.pw-wrap {
  background: #1a1510;
  min-height: 100vh;
  padding: 36px 16px 64px;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #0d0d0d;
}

/* ── Acciones ── */
.pw-actions {
  max-width: 760px;
  margin: 0 auto 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-back {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 18px; border: 1.5px solid rgba(192,157,98,.4);
  border-radius: 8px; background: transparent;
  color: #c09d62; font-size: .82rem; cursor: pointer;
  transition: border-color .2s, background .2s;
}
.btn-back:hover { border-color: #c09d62; background: rgba(192,157,98,.08); }

.btn-print {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 22px; background: #c09d62;
  border: none; border-radius: 8px; color: #0d0d0d;
  font-size: .82rem; font-weight: 800; cursor: pointer;
  letter-spacing: .4px; transition: background .2s, transform .15s;
}
.btn-print:hover:not(:disabled) { background: #d4b07a; transform: translateY(-1px); }
.btn-print:disabled { opacity: .5; cursor: not-allowed; }

/* ── Estado ── */
.pw-estado {
  display: flex; flex-direction: column; align-items: center;
  gap: 14px; padding: 80px 24px; color: #c09d62;
}
.spinner {
  width: 40px; height: 40px;
  border: 3px solid rgba(192,157,98,.2);
  border-top-color: #c09d62;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Hoja ── */
.sheet {
  max-width: 760px;
  margin: 0 auto;
  background: #fdfbf7;
  box-shadow: 0 8px 48px rgba(0,0,0,.5), 0 2px 8px rgba(0,0,0,.3);
}

/* Barra top */
.top-bar { display: grid; grid-template-columns: 3fr 1fr; height: 10px; }
.top-l { background: #0d0d0d; }
.top-r { background: #c09d62; }

/* ── Cabecera ── */
.head {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 32px;
  padding: 28px 36px 24px;
  background: #fff;
  border-bottom: 1.5px solid #e8dfc8;
}
.head-left { display: flex; flex-direction: column; gap: 10px; }
.logo-img  { max-height: 38px; max-width: 200px; object-fit: contain; display: block; }

.prestador-info    { display: flex; flex-direction: column; gap: 2px; }
.prestador-nombre  { font-size: .82rem; font-weight: 700; color: #0d0d0d; }
.prestador-dato    { font-size: .72rem; color: #7a6a4a; line-height: 1.6; }

.head-right { display: flex; flex-direction: column; align-items: flex-end; gap: 16px; }

.doc-title {
  font-size: 2.2rem; font-weight: 900; color: #0d0d0d;
  letter-spacing: 5px; line-height: 1;
  border-bottom: 3px solid #c09d62;
  padding-bottom: 8px;
}

.meta-block { display: flex; flex-direction: column; gap: 8px; text-align: right; }
.meta-lbl   {
  font-size: .65rem; font-weight: 800; color: #c09d62;
  text-transform: uppercase; letter-spacing: 1.2px;
  display: block; margin-bottom: 2px;
}
.meta-val {
  font-size: .8rem; font-weight: 600; color: #0d0d0d;
  border-bottom: 1px dashed #c4b088;
  padding-bottom: 3px;
  display: inline-block; min-width: 140px; text-align: right;
}
.meta-val.vencimiento {
  color: #b84040;
  border-bottom-color: #b84040;
  font-weight: 800;
}

/* ── Cuerpo ── */
.body { padding: 24px 36px; }

.section-lbl {
  font-size: .65rem; font-weight: 900; color: #c09d62;
  text-transform: uppercase; letter-spacing: 2px;
  margin-bottom: 14px;
  display: flex; align-items: center; gap: 10px;
}
.section-lbl::after { content: ''; flex: 1; height: 1px; background: #e8dfc8; }

.client-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 0 32px;
}
.dotted-row {
  display: flex; align-items: baseline; gap: 6px;
  padding: 6px 0; border-bottom: 1px dashed #d4c8a8;
}
.dotted-key { font-size: .72rem; font-weight: 700; color: #0d0d0d; white-space: nowrap; flex-shrink: 0; }
.dotted-val { font-size: .72rem; color: #3a3020; flex: 1; text-align: right; }

.plan-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: #0d0d0d; color: #c09d62;
  font-size: .67rem; font-weight: 800;
  padding: 4px 12px; border-radius: 4px;
  letter-spacing: .8px; text-transform: uppercase;
  margin-top: 14px;
}

/* ── Tabla ── */
.tbl-wrap {
  margin-top: 18px;
  border: 1.5px solid #e8dfc8;
  border-radius: 4px;
  overflow: hidden;
}
table.svc { width: 100%; border-collapse: collapse; font-size: .78rem; }
table.svc thead th {
  background: #0d0d0d; color: #c09d62;
  padding: 10px 14px; font-weight: 800;
  font-size: .67rem; letter-spacing: 1.2px; text-transform: uppercase; text-align: center;
}
table.svc thead th.l { text-align: left; }
table.svc thead th.r { text-align: right; }

table.svc tbody tr:nth-child(odd)  td { background: #fdfbf7; }
table.svc tbody tr:nth-child(even) td { background: #f5f0e6; }

table.svc tbody td {
  padding: 10px 14px; border-bottom: 1px solid #e8dfc8;
  color: #1a1510; text-align: center; vertical-align: top;
}
table.svc tbody td.l { text-align: left; }
table.svc tbody td.r { text-align: right; font-variant-numeric: tabular-nums; font-weight: 700; }
table.svc tbody tr:last-child td { border-bottom: none; }
table.svc tbody tr.empty-row td { height: 30px; }

.svc-name { font-weight: 800; color: #0d0d0d; font-size: .8rem; margin-bottom: 3px; }
.svc-desc { font-size: .71rem; color: #6b5a3a; line-height: 1.55; margin-bottom: 7px; }
.chips    { display: flex; flex-wrap: wrap; gap: 4px; }
.chip {
  background: #f0e8d4; color: #5a4020;
  font-size: .63rem; font-weight: 700;
  padding: 2px 8px; border-radius: 3px;
  border: 1px solid #d4c090; letter-spacing: .2px;
}

/* ── Pie ── */
.foot {
  display: grid; grid-template-columns: 1fr 220px;
  gap: 24px; padding: 20px 36px 24px;
  border-top: 1.5px solid #e8dfc8;
  background: #fff; align-items: start;
}

.obs-titulo {
  font-size: .72rem; font-weight: 800; color: #0d0d0d;
  text-transform: uppercase; letter-spacing: .8px;
  border-bottom: 2px solid #0d0d0d;
  display: inline-block; padding-bottom: 3px; margin-bottom: 10px;
}
.obs-body { font-size: .72rem; color: #5a4a2a; line-height: 1.7; }

.totales {}
.tot-row {
  display: flex; justify-content: space-between; gap: 16px;
  padding: 6px 0; border-bottom: 1px solid #e8dfc8;
}
.tot-lbl { font-size: .73rem; color: #6b5a3a; }
.tot-val { font-size: .73rem; color: #0d0d0d; font-variant-numeric: tabular-nums; font-weight: 600; }

.tot-row-ref td, .tot-row-ref span { opacity: .75; }
.tot-ref { font-style: italic; }

.tot-final {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 10px; background: #0d0d0d; padding: 10px 14px;
}
.tot-final-lbl { font-size: .72rem; font-weight: 900; color: #c09d62; letter-spacing: 1px; text-transform: uppercase; }
.tot-final-val { font-size: 1.05rem; font-weight: 900; color: #c09d62; font-variant-numeric: tabular-nums; }

.tot-disclaimer {
  font-size: .62rem; color: #8a7a5a; line-height: 1.5;
  margin-top: 8px; font-style: italic;
}

.tc-nota {
  display: flex; align-items: flex-start; gap: 7px;
  margin-top: 14px; padding: 10px 14px;
  background: #f5f0e6; border: 1px dashed #d4c090;
  border-radius: 6px; font-size: .72rem; color: #5a4020; line-height: 1.6;
}
.tc-nota i { color: #c09d62; flex-shrink: 0; margin-top: 2px; }

/* Nota legal */
.nota {
  text-align: center; font-size: .63rem; color: #a09070;
  padding: 10px 36px; background: #fff;
  border-top: 1px solid #e8dfc8; line-height: 1.6;
}

/* Barra inferior */
.bottom-bar {
  height: 6px;
  background: linear-gradient(90deg, #0d0d0d 0%, #c09d62 50%, #0d0d0d 100%);
}

/* ── Impresión ── */
@media print {
  .no-print { display: none !important; }
  .pw-wrap  { background: #fff !important; padding: 8mm 10mm !important; min-height: unset !important; }
  .sheet    { box-shadow: none !important; max-width: 100% !important; width: 100% !important; }
  .top-l    { background: #0d0d0d !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .top-r    { background: #c09d62 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table.svc thead th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .tot-final { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .plan-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .bottom-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .head         { grid-template-columns: 1fr; }
  .head-right   { align-items: flex-start; }
  .client-grid  { grid-template-columns: 1fr; }
  .foot         { grid-template-columns: 1fr; }
  .body         { padding: 18px 20px; }
  .foot         { padding: 16px 20px 20px; }
}
</style>

<!-- Estilos GLOBALES de impresión (no scoped) para ocultar el layout de la app -->
<style>
@media print {
  /* Elimina fecha/título/URL del browser al imprimir */
  @page {
    size: A4 portrait;
    margin: 0;
  }

  /* Oculta todo el shell de la app */
  .app-header,
  .app-sidebar,
  .sidebar,
  .app-navbar,
  .app-topbar,
  .topbar,
  header,
  nav,
  .layout-sidebar,
  .main-nav,
  [class*="sidebar"],
  [class*="navbar"],
  [class*="topbar"] {
    display: none !important;
  }

  /* Expande el contenido principal a pantalla completa */
  .main-content,
  .app-content,
  .layout-content,
  .content-area,
  main {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: unset !important;
  }

  /* Evita página en blanco extra */
  .app-wrapper,
  #app {
    min-height: unset !important;
    height: auto !important;
  }

  body, html {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: unset !important;
    height: auto !important;
  }
}
</style>
